import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import Keycloak, { KeycloakConfig, KeycloakInstance } from 'keycloak-js';
import { User } from '../../../shared/types';
import { userAPI } from '../../../shared/services/api';
import apiClient from '../../../shared/services/api';
import toast from 'react-hot-toast';

interface AuthContextType {
  keycloak: KeycloakInstance | null;
  initialized: boolean;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  tokenVersion: number;
  login: () => void;
  register: () => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  syncUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [keycloak, setKeycloak] = useState<KeycloakInstance | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tokenVersion, setTokenVersion] = useState(0);

  useEffect(() => {
    initializeKeycloak();
  }, []);

  const initializeKeycloak = async () => {
    try {
      const keycloakConfig: KeycloakConfig = {
        url: import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080',
        realm: import.meta.env.VITE_KEYCLOAK_REALM || 'bookkids',
        clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'bookkids-frontend',
      };

      const keycloakInstance = new Keycloak(keycloakConfig);

      // Initialize with timeout protection to prevent hanging
      const initializeWithTimeout = async () => {
        const timeoutPromise = new Promise<boolean>((resolve) => {
          setTimeout(() => {
            console.warn('Keycloak initialization timeout after 8 seconds');
            resolve(false);
          }, 8000);
        });

        try {
          const initPromise = keycloakInstance.init({
            onLoad: 'check-sso',
            checkLoginIframe: false,
            silentCheckSsoRedirectUri: window.location.origin + '/silent-check-sso.html'
          });

          return await Promise.race([initPromise, timeoutPromise]);
        } catch (error) {
          console.warn('Keycloak init failed:', error);
          return false;
        }
      };

      const authenticated = await initializeWithTimeout();


      setKeycloak(keycloakInstance);
      setInitialized(true);
      setIsLoading(false);

      if (authenticated && keycloakInstance.token) {
        // Store token immediately
        localStorage.setItem('token', keycloakInstance.token);
        apiClient.defaults.headers.common['Authorization'] = `Bearer ${keycloakInstance.token}`;
        
        await setupAuthenticatedUser(keycloakInstance);
      }

      // Set up token refresh
      if (authenticated) {
        setupTokenRefresh(keycloakInstance);
      }

    } catch (error) {
      console.error('Keycloak initialization failed:', error);
      toast.error('Authentication service unavailable. Some features may be limited.');
      setKeycloak(null);
      setInitialized(true);
    } finally {
      setIsLoading(false);
    }
  };

  const setupAuthenticatedUser = async (keycloakInstance: KeycloakInstance) => {
    try {
      // Set up API client with token
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${keycloakInstance.token}`;
      
      // Sync user with backend
      const response = await userAPI.syncUser();
      setUser(response.data);
      
      // Store token for API interceptor
      if (keycloakInstance.token) {
        localStorage.setItem('token', keycloakInstance.token);
      }
    } catch (error) {
      console.error('Failed to sync user:', error);
      toast.error('Failed to load user data');
    }
  };

  const setupTokenRefresh = (keycloakInstance: KeycloakInstance) => {
    // Refresh token when it's about to expire
    const interval = setInterval(async () => {
      try {
        // Update token if it expires in the next 30 seconds
        const refreshed = await keycloakInstance.updateToken(30);
        if (refreshed && keycloakInstance.token) {
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${keycloakInstance.token}`;
          localStorage.setItem('token', keycloakInstance.token);
          
          // Force re-render by incrementing tokenVersion to trigger useWebSocket re-connection
          setTokenVersion(prev => prev + 1);
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        // Don't logout immediately - user might just need to re-authenticate
        localStorage.removeItem('token');
        delete apiClient.defaults.headers.common['Authorization'];
        setUser(null);
      }
    }, 1 * 60 * 1000); // Check every minute

    // Cleanup on unmount
    return () => clearInterval(interval);
  };

  const handleLogin = () => {
    if (keycloak) {
      // Get current language from i18n
      const currentLanguage = localStorage.getItem('i18nextLng') || 'en';
      const keycloakLanguage = currentLanguage === 'bg' ? 'bg' : 'en';
      
      
      // Use KeycloakLoginOptions with locale and redirectUri
      keycloak.login({
        locale: keycloakLanguage,
      });
    }
  };

  const handleRegister = () => {
    if (keycloak) {
      // Get current language from i18n
      const currentLanguage = localStorage.getItem('i18nextLng') || 'en';
      const keycloakLanguage = currentLanguage === 'bg' ? 'bg' : 'en';


      // Use KeycloakRegisterOptions with locale and redirectUri
      keycloak.register({
        locale: keycloakLanguage,
        redirectUri: import.meta.env.VITE_REDIRECT_URI || window.location.origin
      });
    }
  };

  const handleLogout = async () => {
    
    // If not authenticated to Keycloak, just clear local state and redirect
    if (!keycloak?.authenticated) {
      localStorage.removeItem('token');
      localStorage.clear();
      delete apiClient.defaults.headers.common['Authorization'];
      setUser(null);
      window.location.href = '/';
      return;
    }
    
    // Use Keycloak JS library's logout method for proper session termination
    if (keycloak) {
      try {
        // Clear local state BEFORE logout to prevent interference
        localStorage.removeItem('token');
        delete apiClient.defaults.headers.common['Authorization'];
        setUser(null);
        
        // Call Keycloak logout - this will redirect to Keycloak and back
        await keycloak.logout({
          redirectUri: window.location.origin
        });
      } catch (error) {
        console.error('Keycloak logout failed, falling back to manual logout:', error);
        
        // Clear local state for fallback as well
        localStorage.removeItem('token');
        localStorage.clear();
        delete apiClient.defaults.headers.common['Authorization'];
        setUser(null);
        
        // Fallback to manual logout URL
        const keycloakUrl = import.meta.env.VITE_KEYCLOAK_URL || 'http://localhost:8080';
        const realm = import.meta.env.VITE_KEYCLOAK_REALM || 'bookkids';
        const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'bookkids-frontend';
        const redirectUri = encodeURIComponent(window.location.origin);
        
        const logoutUrl = `${keycloakUrl}/realms/${realm}/protocol/openid-connect/logout?post_logout_redirect_uri=${redirectUri}&client_id=${clientId}`;
        
        window.location.href = logoutUrl;
      }
    }
  };

  const refreshUser = async () => {
    if (!keycloak?.authenticated) return;

    try {
      const response = await userAPI.getCurrentUser();
      setUser(response.data);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  const syncUser = async () => {
    if (!keycloak?.authenticated || !keycloak.token) {
      toast.error('Please log in first');
      return;
    }

    try {
      // Set up API client with current token
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${keycloak.token}`;
      localStorage.setItem('token', keycloak.token);
      
      // Sync user with backend
      const response = await userAPI.syncUser();
      setUser(response.data);
      toast.success('User synced successfully!');
    } catch (error) {
      console.error('Failed to sync user:', error);
      toast.error('Failed to sync user. Please try refreshing the page.');
    }
  };

  const value: AuthContextType = {
    keycloak,
    initialized,
    user,
    isAuthenticated: !!keycloak?.authenticated,
    tokenVersion,
    isLoading,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
    refreshUser,
    syncUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};